<template>
  <div class="vcl-date-picker" :style="{ width }">
    <date-picker v-model="datePickerValue" :mode="mode" :masks="maskOptions" :min-date="minDate" :max-date="maxDate" :is24hr="is24hr" :is-range="isRange" :available-dates="availableDates" :update-on-input="false" :timezone="forceTimezone" :model-config="modelConfig" @dayclick="dayClick($event)">
      <template #default="{ inputValue, togglePopover, updateValue }">
        <vp-input v-if="element === 'input'" :id="'datePicker'+_uid" :value="inputValue" :placeholder="placeholder" width="100%" :required="required" :height="height" :styles="styles" @click="togglePopover" @change.native="update($event.target.value, updateValue)" v-on="listeners">
          <template v-if="$slots.label" #label>
            <slot name="label" />
          </template>
          <template v-if="showIcon && $slots.prefix" #prefix>
            <slot name="prefix" :toggle="togglePopover" />
          </template>
          <template v-if="showIcon" #suffix>
            <slot name="suffix" :toggle="togglePopover">
              <VpIcon icon="calendar" class="vcl-date-picker__icon" :style="iconOptions" @click="togglePopover" />
            </slot>
          </template>
          <template v-if="$slots.error" #error>
            <slot name="error" />
          </template>
        </vp-input>
        <slot v-else name="default" v-bind="{ inputValue, togglePopover, updateValue }"></slot>
      </template>
    </date-picker>
    <div class="vcl-date-picker__timezone" v-if="forceTimezone"> {{ forceTimezone }}</div>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from '@virtual-peaker/v-calendar/src/components/DatePicker.vue';
import VpIcon from '../vp-icon/VpIcon.vue';
import VpInput from '../vp-input/VpInput.vue';

export default {
  name: 'VpDatePicker',
  components: {
    VpIcon,
    VpInput,
    DatePicker,
  },
  props: {
    element: {
      type: String,
      default: 'input',
    },
    mode: {
      type: String,
      default: 'date',
    },
    value: {
      type: [String, Date],
      default: null
    },
    format: {
      type: String,
      default: null,
    },
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '45px',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      default: null,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    icon: {
      type: Object,
      default() {
        return {};
      },
    },
    minDate: {
      type: Date,
      default: null,
    },
    maxDate: {
      type: Date,
      default: null,
    },
    styles: {
      type: Object,
      default() {
        return {};
      },
    },
    is24hr: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    availableDates: {
      type: Object,
      default: undefined,
    },
    isRange: {
      type: Boolean,
      default: false,
    },
    forceTimezone: {
      type: String,
      default: null,
    },
    timeAdjust: {
      type: String,
      default: null,
    }
  },
  emits: ['input', 'dayclick'],
  data() {
    return {
      datePickerValue: this.value || null,
    };
  },
  computed: {
    iconOptions() {
      let options = {
        color: '#016699',
        fontSize: '12px',
      };
      if (this.icon) {
        options = { ...options, ...this.icon };
      }
      return options;
    },
    maskOptions() {
      let options = {};
      if (this.format) {
        if (this.mode === 'date') {
          options = { input: this.format };
        } else if (this.mode === 'dateTime') {
          options = this.is24hr ? { inputDateTime24hr: this.format } : { inputDateTime: this.format };
        }
      }
      return options;
    },
    listeners() {
      const {
        input, click, change, ...listeners
      } = this.$listeners;
      return listeners;
    },
    modelConfig() {
      const config = {};
      if (this.forceTimezone && this.mode === 'date') {
        // Default to start of day based on timezone
        config.timeAdjust = this.timeAdjust || '00:00:00'
      }
      return config;
    }
  },
  watch: {
    datePickerValue: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.$emit('input', '');
          return null;
        }
        if (!moment(val, this.format, true).isValid()) {
          val = this.isRange ? {
          start: moment(val.start).format(this.format),
          end: moment(val.end).format(this.format)
          } : moment(val).format(this.format);
        }
        this.$emit('input', val);
      },
    },
    value: {
      immediate: true,
      handler(val) {
        this.datePickerValue = val;
      }
    },
  },
  methods: {
    update(value, updateValue) {
      const parsedValue = value.includes('-') ? value.replace(/-/g, '/') : value;
      const updatedValue = moment(parsedValue, this.format).toISOString();
      return updateValue(updatedValue);
    },
    dayClick(event){
      if(this.mode === 'date') {
        this.$emit('dayclick', event.date);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .vcl-date-picker {
    $self: &;
    margin: 0;
    padding: 0;
    &__icon {
      font-size: 12px;
    }
    &__timezone {
      font-size: 0.8em;
      text-align: end;
    }
  }

  .vcl-date-picker__icon {
    cursor:pointer;
  }

</style>
